// Google is requested only after a visitor activates a map. Share one loader per page.
let apiPromise;
let authenticationFailed = false;
const authSubscribers = new Set();
let authInstalled = false;

export function loadMaps(apiKey) {
  if (!apiKey || authenticationFailed) return Promise.reject(new Error('Map unavailable'));
  if (apiPromise) return apiPromise;
  if (!authInstalled) {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => {
      authenticationFailed = true;
      authSubscribers.forEach(notify => notify());
      if (typeof previous === 'function') previous();
    };
    authInstalled = true;
  }
  if (window.google?.maps?.Map && window.google.maps.OverlayView) return Promise.resolve(window.google.maps);
  apiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const callback = '__aimMapsReady';
    let settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      authSubscribers.delete(onAuthFailure);
      window[callback] = () => {};
      if (error) {
        script.remove();
        reject(error);
      } else resolve(window.google.maps);
    };
    const onAuthFailure = () => finish(new Error('Map authentication failed'));
    authSubscribers.add(onAuthFailure);
    const timer = setTimeout(() => finish(new Error('Map load timed out')), 15000);
    window[callback] = () => finish(authenticationFailed || !window.google?.maps?.Map ? new Error('Map unavailable') : null);
    script.onerror = () => finish(new Error('Map could not load'));
    const params = new URLSearchParams({key:apiKey, loading:'async', callback, v:'quarterly'});
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.dataset.aimMaps = '';
    document.head.append(script);
  });
  return apiPromise;
}

function logoPin(maps, map, config) {
  const position = new maps.LatLng(config.position.lat, config.position.lng);
  class AimPin extends maps.OverlayView {
    onAdd() {
      this.element = document.createElement('a');
      this.element.className = 'location-map-pin';
      this.element.href = config.directionsUrl;
      this.element.setAttribute('aria-label', `Open ${config.locationName || 'this location'} in Google Maps`);
      const image = document.createElement('img');
      image.src = config.logoUrl;
      image.alt = '';
      image.width = 62;
      image.height = 56;
      this.element.append(image);
      this.getPanes().overlayMouseTarget.append(this.element);
      maps.OverlayView.preventMapHitsAndGesturesFrom(this.element);
    }
    draw() {
      const point = this.getProjection()?.fromLatLngToDivPixel(position);
      if (!point || !this.element) return;
      this.element.style.left = `${point.x}px`;
      this.element.style.top = `${point.y}px`;
    }
    onRemove() { this.element?.remove(); this.element = null; }
  }
  const pin = new AimPin();
  pin.setMap(map);
  return pin;
}

export default function locationMap() {
  // Keep vendor instances out of Alpine's reactive proxy.
  let config, map, pin, maps, disposed = false, unsubscribe;
  const clear = () => {
    pin?.setMap(null);
    if (map && maps) maps.event.clearInstanceListeners(map);
    pin = map = null;
  };
  return {
    state:'idle',
    init() {
      try { config = JSON.parse(this.$el.dataset.mapConfig); }
      catch { this.state = 'error'; return; }
      if (!config.position || !config.apiKey) return;
      const onAuthFailure = () => { if (!disposed) { clear(); this.state = 'error'; } };
      authSubscribers.add(onAuthFailure);
      unsubscribe = () => authSubscribers.delete(onAuthFailure);
    },
    async load() {
      if (this.state !== 'idle' || !config?.position || !config.apiKey) return;
      this.state = 'loading';
      try {
        maps = await loadMaps(config.apiKey);
        if (disposed || authenticationFailed) return;
        await this.$nextTick();
        if (disposed || authenticationFailed) return;
        map = new maps.Map(this.$refs.canvas, {
          center:config.position, zoom:config.zoom,
          gestureHandling:'cooperative', scrollwheel:false, keyboardShortcuts:true,
          mapTypeControl:false, streetViewControl:false, fullscreenControl:true,
          zoomControl:true, clickableIcons:false,
        });
        pin = logoPin(maps, map, config);
        this.state = 'ready';
      } catch {
        if (!disposed) { clear(); this.state = 'error'; }
      }
    },
    destroy() {
      disposed = true;
      unsubscribe?.();
      clear();
    },
  };
}
